import type {JSX} from 'react'
import {useState, useEffect, useCallback, useMemo} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import {getCookie, setCookies} from 'cookies-next'
import {useRouter} from 'next/router'
import {Button as CButton} from '@/_new-code/products/flexible-web-toolkit/components/button/button'
import {RichTextRenderer} from '@/_new-code/products/flexible-web-toolkit/components/rich-text-renderer'
import type {
	AdvancedPageTemplateContentItem,
	GlobalConfigContentItem,
	Tersed,
} from '@/_new-code/services/kontent-ai/types'
import type {GeoLocationApiResponse} from '@/_new-code/services/geo-location-popup/geo-location-api-handler'
import type {Result} from '@/_new-code/utilities/types'
import type {BlockMapper as Mapper} from '@/_new-code/services/kontent-ai/mapper'
import {pushToDataLayer} from '@/utils/analytics'
import {loadImageFromKontentAI} from '../../../imageLoader'
import {ImageBlock} from '../flexible-web-toolkit/blocks/image'

function findHrefsByCountryCode(
	hreflangs: string,
	countryCode: string
): string[] {
	const regex =
		/<link\s+rel="alternate"\s+href="(?<href>[^"]+)"\s+hreflang="(?<hreflang>[^"]+)"\s*\/?>/g
	const matches: string[] = []
	const lowerCountryCode = countryCode.toLowerCase()
	let match: RegExpExecArray | null

	while ((match = regex.exec(hreflangs)) !== null) {
		const href = match.groups?.href ?? ''
		const hreflang = match.groups?.hreflang?.toLowerCase()
		const langParts = hreflang?.split('-')
		if (
			(langParts && langParts[1] === lowerCountryCode) ||
			hreflang === lowerCountryCode
		) {
			matches.push(href)
		}
	}
	return matches
}

export const GeoLocationBlock = ({
	globalConfig,
	page,
	BlockMapper,
}: {
	globalConfig: Tersed<GlobalConfigContentItem>
	page: Tersed<AdvancedPageTemplateContentItem>
	BlockMapper: typeof Mapper
}): JSX.Element => {
	const router = useRouter()
	const [showBanner, setShowBanner] = useState(false)
	const [countryFlag, setCountryFlag] =
		useState<GeoLocationApiResponse | null>(null)
	const [isLoadingFlag, setIsLoadingFlag] = useState(false)
	const [updatedContentValue, setUpdatedContentValue] = useState('')
	const [updatedLinkTextValue, setUpdatedLinkTextValue] = useState('')
	const cookie = getCookie('X-Origin-Country')
	const [originCountry, setOriginCountry] = useState<string>(() =>
		typeof cookie === 'string' ? cookie : ''
	)

	const {
		title,
		content,
		button,
		linkText,
		defaultImage,
		defaultContent,
		defaultButton,
	} = globalConfig.elements.geolocationPopup[0]?.elements ?? {}

	const fetchCountryFlag = useCallback(
		async (originCountryCode: string) => {
			setIsLoadingFlag(true)
			try {
				const response = await fetch('/api/geo-location/info', {
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						originCountry: originCountryCode,
						locale: router.locale,
					}),
				})
				if (response.ok) {
					const result =
						(await response.json()) as Result<GeoLocationApiResponse>
					if (result.success) setCountryFlag(result.data)
				}
			} catch (error) {
				// eslint-disable-next-line no-console -- to check the issue
				console.error('GeoLocation fetch error:', error)
			} finally {
				setIsLoadingFlag(false)
			}
		},
		[router.locale]
	)

	useEffect(() => {
		const cookieValue = getCookie('X-Origin-Country')
		if (cookieValue && cookieValue !== originCountry) {
			setOriginCountry(typeof cookieValue === 'string' ? cookieValue : '')
		}
	}, [])

	useEffect(() => {
		if (countryFlag && content?.value) {
			setUpdatedContentValue(
				content.value
					.replaceAll(
						'{{originCountry}}',
						countryFlag.origin.countries
							.map((x) => x.name)
							.join('/') || ''
					)
					.replaceAll(
						'{{currentSite}}',
						countryFlag.requested.countryName || ''
					)
			)
			setUpdatedLinkTextValue(
				linkText?.replaceAll(
					'{{currentSiteCode}}',
					countryFlag.requested.countryName || ''
				) ?? ''
			)
		}
	}, [countryFlag, content?.value, linkText, button])

	useEffect(() => {
		const showGeoLocationBlock =
			originCountry &&
			globalConfig.elements.geolocationPopup[0] &&
			originCountry !== router.locale &&
			router.locale !== 'global' &&
			getCookie('isGeolocationPromptHandled') !== true
		if (showGeoLocationBlock) {
			void fetchCountryFlag(originCountry)
			setShowBanner(showGeoLocationBlock)
			pushToDataLayer({
				event: 'geolocation_popup_shown',
				user_country: originCountry.toLowerCase(),
				page_url: window.location.href,
			})
		}
	}, [
		fetchCountryFlag,
		globalConfig.elements.geolocationPopup,
		originCountry,
		router.locale,
	])

	const handleCtaButtons = async (url: string): Promise<void> => {
		let redirectPath = ''
		const hrefs = findHrefsByCountryCode(
			page.elements.snippetPageSeoPageHreflangs,
			originCountry
		)
		redirectPath = hrefs[0] || url

		setCookies('isGeolocationPromptHandled', true)
		setShowBanner(false)

		const analyticsPromise = new Promise<void>((resolve) => {
			pushToDataLayer({
				event: 'geolocation_popup_confirmed',
				user_country: originCountry.toLowerCase(),
				page_url: window.location.href,
				redirect_url: redirectPath,
			})

			resolve()
		})

		try {
			await analyticsPromise
			window.location.href = redirectPath
		} catch (error) {
			// eslint-disable-next-line no-console -- Assertions are correct
			console.error(
				'Redirect failed, falling back to location.href',
				error
			)
			window.location.href = redirectPath
		}
	}
	const imageDisplay = useMemo(() => {
		if (isLoadingFlag) {
			return (
				<div className="mb-4 flex justify-center">
					<div className="flex animate-pulse items-center gap-3">
						<div className="h-[50px] w-[50px] rounded bg-gray-300" />
						<div className="h-4 w-24 rounded bg-gray-300" />
					</div>
				</div>
			)
		} else if (countryFlag?.origin.flagImage?.[0]) {
			return (
				<div className="mb-4 flex justify-center">
					<div className="flex items-center gap-3">
						<Image
							alt={
								countryFlag.origin.countries[0]?.name ||
								'Country flag'
							}
							className="flex-shrink-0 rounded"
							height={50}
							loader={loadImageFromKontentAI}
							src={countryFlag.origin.flagImage[0].url}
							width={50}
						/>
					</div>
				</div>
			)
		} else if (defaultImage?.[0]) {
			return (
				<div className="mb-4 flex justify-center">
					<div className="flex items-center gap-3">
						<ImageBlock
							BlockMapper={BlockMapper}
							block={defaultImage[0]}
							globalConfig={globalConfig}
							key={defaultImage[0].system.id}
							page={page}
						/>
					</div>
				</div>
			)
		}
		return null
	}, [
		isLoadingFlag,
		countryFlag,
		defaultImage,
		BlockMapper,
		globalConfig,
		page,
	])

	const richTextContent = useMemo(() => {
		if (content && countryFlag?.origin.flagImage?.[0]) {
			return (
				<RichTextRenderer
					BlockMapper={BlockMapper}
					className="justify-left pl-5 pr-5 text-center text-[#373737]"
					data-kontent-element-codename="content"
					element={{
						...content,
						value: updatedContentValue || content.value,
					}}
					globalConfig={globalConfig}
					page={page}
				/>
			)
		} else if (defaultContent) {
			return (
				<RichTextRenderer
					BlockMapper={BlockMapper}
					className="justify-left pl-5 pr-5 text-center text-[#373737]"
					data-kontent-element-codename="default_content"
					element={defaultContent}
					globalConfig={globalConfig}
					page={page}
				/>
			)
		}
		return null
	}, [
		content,
		defaultContent,
		updatedContentValue,
		countryFlag,
		BlockMapper,
		globalConfig,
		page,
	])

	if (!showBanner) {
		// eslint-disable-next-line react/jsx-no-useless-fragment -- no need to show anything
		return <></>
	}

	return (
		<div
			className="fixed inset-y-0 z-[9999] w-full backdrop-blur-[5px] backdrop-filter"
			data-testid="geo-popup"
		>
			<div className="m-auto h-screen bg-[rgba(28,28,28,0.76)] px-4 pb-4 pt-20">
				<div
					className="mx-auto my-0 min-h-[331px] w-[661px] max-w-2xl xxs:min-h-[400px] xxs:w-[100%] xs:min-h-[400px] xs:w-[100%]"
					style={{
						border: 'thick solid #F8F8F8',
						backgroundColor: '#F8F8F8',
						borderRadius: '10px',
						minWidth: '300px',
					}}
				>
					<div className="bg-[#F8F8F8] pb-5 pt-[30px]">
						<div className="pl-5 pr-5 text-center text-[#373737]">
							<p className="text-2xl font-extrabold leading-[1.18]">
								{title}
							</p>
						</div>
					</div>

					<div className=" overflow-y-auto overflow-x-hidden border-t-[1px] border-[lightgrey] bg-[#F8F8F8] pb-[15px] pt-[15px]">
						{imageDisplay}
						{richTextContent}
					</div>

					<div className="mx-auto my-4 flex w-full flex-col items-center justify-center bg-[#F8F8F8] px-4">
						<div className="mb-3 flex w-full flex-col items-center justify-center sm:flex-row sm:gap-3">
							{countryFlag?.origin.countries.length ? (
								countryFlag.origin.countries.map((country) => {
									return (
										<CButton
											className="mb-3 w-auto max-w-[calc(100%-2rem)] px-6 sm:mb-0 sm:max-w-none"
											key={country.name}
											onClick={async () => {
												await handleCtaButtons(
													country.homePage
												)
											}}
										>
											{button?.[0]?.elements.text.replaceAll(
												'{{originCountryCode}}',
												country.name
											)}
										</CButton>
									)
								})
							) : (
								<Link
									className="btn mb-3 inline-flex w-auto max-w-[calc(100%-2rem)] cursor-pointer  items-center justify-center overflow-hidden rounded-sm border-2 border-primary bg-theme-main px-4 px-6 py-3 text-center align-middle font-bold text-white hover:bg-theme-highlight focus:bg-secondary sm:mb-0"
									href="/country-selector?redirected=true"
									locale={false}
									onClick={() => {
										setShowBanner(false)
										setCookies(
											'isGeolocationPromptHandled',
											true
										)
										pushToDataLayer({
											event: 'geolocation_popup_confirmed',
											user_country:
												originCountry.toLowerCase(),
											page_url: window.location.href,
											redirect_url:
												'/country-selector?redirected=true',
										})
									}}
								>
									{defaultButton?.[0]?.elements.text}
								</Link>
							)}
						</div>
						<Link
							className="text-center text-sm hover:underline"
							href={router.asPath}
							onClick={() => {
								setShowBanner(false)
								setCookies('isGeolocationPromptHandled', true)
								pushToDataLayer({
									event: 'geolocation_popup_confirmed',
									user_country: originCountry.toLowerCase(),
									page_url: window.location.href,
									redirect_url: window.location.href,
								})
							}}
						>
							{updatedLinkTextValue}
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}
